//------------------------------------------------------------------------------------
//	Polygonia: Polygon Transforms
//
//
//
//------------------------------------------------------------------------------------

import { BasicUnits, PolygonMiterType, PolygonList, MathUtil, SegmentList, Polygon_FindBounds } from "../../VectorUtilsJS/src/VectorUtilLib.js";
import { Transform } from "../../VectorUtilsJS/src/VectorUtilLib.js";
import { GfxPath, GfxDrawing } from "../../GfxContainer/src/GfxDrawing.js";
import { GfxRender } from "../../GfxContainer/src/GfxRender.js";


//------------------------------------------------------------------------------------
//	Find Bounds
//		Find the minimum and maximum x and y values across all of the polygons
//------------------------------------------------------------------------------------
var FindPolygonListBounds = function(polygonList)
{
	let maxcb = ( max, cur ) => Math.max( max, cur );
	let mincb = ( max, cur ) => Math.min( max, cur );

	let bounds = {min:{x:Infinity, y:Infinity}, max:{x:-Infinity, y:-Infinity}};

	polygonList.polygons.forEach(poly =>
	{
		bounds.min.x = poly.points.map(pt => pt.x).reduce( mincb, bounds.min.x );
		bounds.min.y = poly.points.map(pt => pt.y).reduce( mincb, bounds.min.y );
		bounds.max.x = poly.points.map(pt => pt.x).reduce( maxcb, bounds.max.x );
		bounds.max.y = poly.points.map(pt => pt.y).reduce( maxcb, bounds.max.y );
	});

	return bounds;
}


//------------------------------------------------------------------------------------
//	Draw Closed Poly
//		Calls MoveTo and LineTo for the points of the polygon
//------------------------------------------------------------------------------------
var DrawClosedPoly = function(path, points)
{
	path.MoveTo(points[0]);
	
	for (var i = 1; i < points.length; i++)
		path.LineTo(points[i]);
	
	path.Close();
}

//------------------------------------------------------------------------------------
//	Calc Point Between
//		Return a point between two points, according to a value from 0.0 to 1.0
//		Can also be called with values outside of this range to get points before
//		or after the line
//------------------------------------------------------------------------------------
var CalcPointBetween = function(ptA, ptB, t)
{
	let x = t * ptA.x + (1 - t) * ptB.x;
	let y = t * ptA.y + (1 - t) * ptB.y;
	
	return {x, y};
}

//------------------------------------------------------------------------------------
//	Distance Between
//		Calculates the distance between two points
//------------------------------------------------------------------------------------
var DistanceBetween = function(ptA, ptB)
{
	let x = ptA.x - ptB.x;
	let y = ptA.y - ptB.y;
	
	return Math.sqrt(x*x + y*y);
}

//------------------------------------------------------------------------------------
//	Unit Vectors
//		Returns a unit vector pointing from A to B
//------------------------------------------------------------------------------------
var UnitVector = function(ptA, ptB)
{
	let d = DistanceBetween(ptA, ptB);
	
	let x = ptB.x - ptA.x;
	let y = ptB.y - ptA.y;
	
	return {x:x/d, y:y/d};
}

//------------------------------------------------------------------------------------
//	Calc Approximate Center (of a polygon)
//		Find the approximate center of a polygon by averaging all of the points
//------------------------------------------------------------------------------------
var CalcApproximateCenter = function(points)
{
	let ctr = {x:0, y:0};
	
	points.forEach(pt => { ctr.x += pt.x; ctr.y += pt.y });
	ctr.x /= points.length;
	ctr.y /= points.length;
	
	return ctr;
}



//------------------------------------------------------------------------------------
//	Polygon SelfIntersects
//------------------------------------------------------------------------------------
var PolygonSelfIntersects = function(points)
{
	var selfIntersects = false;
	
	let len = points.length;
	
	for (var i = 0; i < len && !selfIntersects; i++)
	{
		let ptAa = points[i];
		let ptAb = points[(i + 1) % len];
		for (var j = i + 1; j < len && !selfIntersects; j++)
		{
			let ptBa = points[j];
			let ptBb = points[(j + 1) % len];
			
			let c = MathUtil.CalcSegmentIntersection(ptAa, ptAb, ptBa, ptBb);
			
			selfIntersects = (c != undefined);
		}
	}
	
	return selfIntersects;
}

	

//=====================================================================================
//
//	Basic Polygon Transform
//
//=====================================================================================

var BasicPolygonTransform = function(gfxPath, polygon, theSettings)
{
	DrawClosedPoly(gfxPath, polygon.points);
}


//=====================================================================================
//
//	LinesToCenter Polygon Transform
//
//=====================================================================================

var LinesToCenterPolygonTransform = function(gfxPath, polygon, theSettings)
{
	let polyCtr = (polygon.info && polygon.info.ctr ? polygon.info.ctr : CalcApproximateCenter(polygon.points));
	let minSpacing = 0.1;
	let sp = Math.max(minSpacing, theSettings.spacing);
		
	if (theSettings.includePolygon)
		DrawClosedPoly(gfxPath, polygon.points);

	for (var i = 0; i < polygon.points.length; i++)
	{
		let ptA = polygon.points[i];
		let ptB = polygon.points[(i + 1) % polygon.points.length];
		let d = DistanceBetween(ptA, ptB);
		let u = UnitVector(ptA, ptB);
		
		var count = Math.floor(d/sp);
		
		if (count > 1)
		{
			for (var j = 1; j < count; j++)
			{
				let pt = CalcPointBetween(ptA, ptB, j/count);
				gfxPath.MoveTo(polyCtr);
				gfxPath.LineTo(pt);
			}
		}
		
		gfxPath.MoveTo(polyCtr);
		gfxPath.LineTo(polygon.points[i]);
	}
}


//=====================================================================================
//
//	SpiralPolygonTransform
//
//=====================================================================================

var SpiralPolygonTransform = function(gfxPath, polygon, theSettings)
{
	let pts = polygon.points;
	let polyCtr = (polygon.info && polygon.info.ctr ? polygon.info.ctr : CalcApproximateCenter(pts));
	let len = pts.length;
	
	let d = 0;
	var pt;

	// Find average distance to center
	pts.forEach(pt => { d += DistanceBetween(pt, polyCtr)} );
	d /= len;

	
	let minSpacing = 0.5;
	let sp = minSpacing + theSettings.spacing;
	let loops = d / sp;
	
	if (loops < 1 || !isFinite(loops))
		loops = 1;
	
	//let maxLoops = 8;
	//let loops = 1 + maxLoops * theSettings.tParam;

	let steps = Math.floor(loops * len);
	let s = 1/(steps);

	let count = 0;
	
	if (theSettings.includePolygon)
		DrawClosedPoly(gfxPath, polygon.points);

	gfxPath.MoveTo(pts[0]);

	do
	{
		pt = CalcPointBetween(pts[count % len], polyCtr, (steps - count) * s);
		gfxPath.LineTo(pt);
		count++;
	}
	while (/*DistanceBetween(pt, polyCtr) > 2 &&*/ count < steps);
}

//=====================================================================================
//
//	Strings Polygon Transform
//
//=====================================================================================
var StringsPolygonTransform = function(gfxPath, poly, theSettings)
{
	let len = poly.points.length;
	let count = 0;
	var steps = theSettings.count;
	let ignoreConcave = theSettings.ignoreConcave;
	let mode = theSettings.stringStyle;
	
	var ptsA = poly.points;
	
	if (theSettings.includePolygon)
		DrawClosedPoly(gfxPath, poly.points);
	
	for (var i = 1; i < steps; i++)
	{
		for (var j = 0; j < len; j++)
		{
			var addLine = true;
			let r = i/steps;
			let p0 = ptsA[j];
			let p1 = ptsA[(j + 1) % len];
			let p2 = ptsA[(j + 2) % len];
			let p3 = ptsA[(j + 3) % len];
			var ptA, ptB;
			
			if (mode == 0)
			{
				addLine = !ignoreConcave || (MathUtil.CalcVertexAngle(p0, p1, p2) > 0);
				ptA = CalcPointBetween(p0, p1, r);
				ptB = CalcPointBetween(p1, p2, r);
			}
			else if (mode == 1)
			{
				addLine = !ignoreConcave || (MathUtil.CalcAngleBetweenLines({ptA:p0, ptB:p2}, {ptA:p1, ptB:p3}) > 0);
				ptA = CalcPointBetween(p0, p2, r);
				ptB = CalcPointBetween(p1, p3, r);
			}
			else if (mode == 2)
			{
				addLine = !ignoreConcave || (MathUtil.CalcAngleBetweenLines({ptA:p0, ptB:p1}, {ptA:p2, ptB:p3}) > 0);
				ptA = CalcPointBetween(p0, p1, r);
				ptB = CalcPointBetween(p2, p3, r);
			}

			if (addLine)
			{
				gfxPath.MoveTo(ptA);
				gfxPath.LineTo(ptB);
			}
		}
	}
}



//=====================================================================================
//
//	Twist Polygon Transform
//
//=====================================================================================

var TwistPolygonTransform = function(gfxPath, poly, theSettings)
{
	if (theSettings.twistStyle == 0)
		TwistPolygonTransform_StyleA(gfxPath, poly, theSettings);
	else if (theSettings.twistStyle == 1)
		TwistPolygonTransform_StyleB(gfxPath, poly, theSettings);
	else if (theSettings.twistStyle == 2)
		TwistPolygonTransform_StyleC(gfxPath, poly, theSettings);

}

//------------------------------------------------------------------------------------
//	Twist, Style A
//------------------------------------------------------------------------------------
var TwistPolygonTransform_StyleA = function(gfxPath, poly, theSettings)
{
	let len = poly.points.length;
	let count = 0;
	var t = 0.2;
	
	t = theSettings.tParam;
	
	DrawClosedPoly(gfxPath, poly.points);
	
	var ptsA = poly.points;
	var ptsB = [];
	
	do
	{
		for (var i = 0; i < len; i++)
			ptsB[i] = CalcPointBetween(ptsA[i], ptsA[(i + 1) % len], t);

		DrawClosedPoly(gfxPath, ptsB);

		ptsA = ptsB.map(pt => {return {x:pt.x, y:pt.y}});
		count++;
	}
	while (DistanceBetween(ptsA[0], ptsA[1]) > 2 && count < 100);
}




//------------------------------------------------------------------------------------
//	Twist, Style B
//------------------------------------------------------------------------------------
var TwistPolygonTransform_StyleB = function(gfxPath, poly, theSettings)
{
	let len = poly.points.length;
	let count = 0;
	var t = 0.2;
	
	t = theSettings.tParam;
	
	DrawClosedPoly(gfxPath, poly.points);
	
	var ptsA = poly.points;
	var ptsB = [];
	var info = [];
	var someConcave = false;
	
	do
	{
		// Find the angles
		for (var i = 0; i < len; i++)
		{
			let p0 = ptsA[(i + len - 1) % len];
			let p1 = ptsA[i];
			let p2 = ptsA[(i + 1) % len];
			let a = MathUtil.CalcVertexAngle(p0, p1, p2);
			info[i] = {angle:a};
		}
		
		// If all of the angles are concave, then exit
		if (!info.some(e => e.angle > 0))
		{
			console.log("all concave");
			break;
		}
		
		someConcave = info.some(e => e.angle < 0);

		// For all of the convex angles, find the offset point
		for (var i = 0; i < len; i++)
		{
			if (info[i].angle >= 0)
				ptsB[i] = CalcPointBetween(ptsA[i], ptsA[(i + 1) % len], (1-t));
			else
				ptsB[i] = undefined;
		}
		
		while (ptsB.some(p => p == undefined))
		{
			// Find a vertex where the next vertex has a calculated point
			var idx = 0;
			while ((ptsB[idx] != undefined) || (ptsB[idx] == undefined && ptsB[(idx + 1) % len] == undefined))
				idx++;
			
			var pA = ptsB[(idx + 1) % len];
			var pD = ptsA[idx];
			var pt = {};
			pt.x = (pD.x - t * pA.x)/(1 - t);
			pt.y = (pD.y - t * pA.y)/(1 - t);
			ptsB[idx] = pt;
		} 
		
		if (!someConcave || !PolygonSelfIntersects(ptsB))
			DrawClosedPoly(gfxPath, ptsB);
		else if (!PolygonSelfIntersects(ptsB))
			break;

		ptsA = ptsB.map(pt => {return {x:pt.x, y:pt.y}});
		count++;
	}
	while (DistanceBetween(ptsA[0], ptsA[1]) > 2 && ((!someConcave && count < 100) || (someConcave && count < 10) ) );
}

//------------------------------------------------------------------------------------
//	Twist, Style C
//------------------------------------------------------------------------------------
var TwistPolygonTransform_StyleC = function(gfxPath, poly, theSettings)
{
	let len = poly.points.length;
	let count = 0;
	var t = 0.2;
	
	t = theSettings.tParam;
	
	DrawClosedPoly(gfxPath, poly.points);
	
	var ptsA = poly.points;
	var ptsB = [];
	var info = [];
	var someConcave = false;
	
	do
	{
		// Find the angles
		for (var i = 0; i < len; i++)
		{
			let p0 = ptsA[(i + len - 1) % len];
			let p1 = ptsA[i];
			let p2 = ptsA[(i + 1) % len];
			let a = MathUtil.CalcVertexAngle(p0, p1, p2);
			info[i] = {angle:a};
		}
		
		// If all of the angles are concave, then exit
		if (!info.some(e => e.angle > 0))
		{
			console.log("all concave");
			break;
		}
		
		someConcave = info.some(e => e.angle < 0);

		// For all of the convex angles, find the offset point
		for (var i = 0; i < len; i++)
		{
			if (info[i].angle >= 0)
				ptsB[i] = CalcPointBetween(ptsA[i], ptsA[(i + 1) % len], (1-t));
			else
				ptsB[i] = ptsA[i];
		}
		
		DrawClosedPoly(gfxPath, ptsB);

		ptsA = ptsB.map(pt => {return {x:pt.x, y:pt.y}});
		count++;
	}
	while (DistanceBetween(ptsA[0], ptsA[1]) > 2 && ((!someConcave && count < 100) || (someConcave && count < 30) ) );
}

//=====================================================================================
//
//	Get Polygon Transform List
//
// 		Common settings
// 			line width
// 			color
// 		Basic
// 			none
// 		Line to Center
// 			spacing
// 			include polygon
// 		Spiral
// 			spacing (or count)
// 			include polygon
// 		Strings
// 			count
// 			strings_style
// 			ignore reverse corners
// 		Twist
// 			percent (t)
// 			min length
// 			max iterations
// 			coalesce short lines
// 			twist_style: reverse corners
// 				treat as regular
// 				reverse twist
// 				no movement
//=====================================================================================
function GetPolygonTransformList() 
{
	var PolygonTransformList = [
		{	name: "Basic",
			id: "Basic",
			func: BasicPolygonTransform,
			settings: [] },
		
		{	name: "Line to Center",
			id: "Center",
			func: LinesToCenterPolygonTransform,
			settings: [ "pSpacing", "pIncludePolygon" ] },
		
		{	name: "Spiral",
			id: "Spiral",
			func: SpiralPolygonTransform,
			settings: [ "pSpacing", "pIncludePolygon" ] },
		
		{	name: "Strings",
			id: "Strings",
			func: StringsPolygonTransform,
			settings: [ "pCount", "pIgnoreConcave", "pStringStyle", "pIncludePolygon" ] },
		
		{	name: "Twist",
			id: "Twist",
			func: TwistPolygonTransform,
			settings: [ "pPercent", /*"pMinLength", "pMaxIterations",*/ /*"pCoalesceShort",*/ /*"pCoalesceStraight", */ "pTwistStyle" ] }
		];
	
	
	return PolygonTransformList;
}

//-------------------------------------------------------------------------------------
//	Public
//-------------------------------------------------------------------------------------
export { GetPolygonTransformList };
